import { cloneDeep, matchesProperty, merge } from "lodash";
import { defineStore } from "pinia";
import { instance } from "@/api";
import { getRoles, getDetails, init, updateUser } from "@/api/apiclient/user";

import { clearToken, keycloak, logout } from "@/utilities/keycloak";
import { LocalStorage } from "@/storage";

import type { privilegeType, userType } from "@/types/user";
import type { profileAgreementType } from "@/types/agreements";
import {
  addProfileSetting,
  deleteProfileSetting,
  getProfileSetting,
} from "@/api/apiclient/profile";
import { permissionType } from "@/types/permission";
import { useSettings } from "./settings";

const model: userType = {
  acceptedAgreements: [],
  avatar: null,
  bio: null,
  dob: null,
  email: null,
  enabled: true,
  firstName: null,
  fiscalCode: null,
  id: null,
  lastName: null,
  password: null,
  phone: null,
  IdentityInfos: {
    email: null,
    email_verified: null,
    family_name: null,
    given_name: null,
    name: null,
    preferred_username: null,
    sub: null,
  },
  profileAgreement: [],
  organizations: [],
  selectedSkills: [],
  userType: null,
  skillDescription: null,
  role: null,
};

const professionalCategories = [
  { key: "Surveyor", label: "Geometra" },
  { key: "Architect", label: "Architetto" },
  { key: "Engineer", label: "Ingegnere" },
  { key: "StructuralEngineer", label: "Ingegnere strutturale" },
  { key: "BusinessConsultant", label: "Consulente aziendale" },
  { key: "Expert", label: "Perito" },
];

export const useUser = defineStore("user", {
  state: () => {
    return {
      // If the model evolves into having nested references we avoid gotchas
      current: cloneDeep(model),
      refreshToken: null as string | null | undefined,
      token: null as string | null | undefined,
      idToken: null as string | null | undefined,
      roles: [] as any[],
      allRoles: [] as any[],
      network: {} as {
        code: string;
        id: number;
        name: string;
        type: string;
      },
      missingAgreements: [] as {
        content: string;
        description: string;
        id: number;
        isMandatory: boolean;
        path: null | string;
        title: null | string;
        type: string;
        version: number;
      }[],
      role_id: null,
      currentRole: null as string | null | undefined,
      privileges: [] as Array<privilegeType>,
      has_bank_service_projects: false,
      savedRole: null as { id: number; name: string } | null,
      availableRoles: [] as Array<{ id: number; name: string }>,
      permissions: [] as Array<permissionType>,
    };
  },
  actions: {
    setCurrent(item: userType): void {
      this.current = merge(this.current, item);
    },
    resetCurrent(): void {
      this.current = cloneDeep(model);
    },
    async getCurrent(params: { withIdentity: boolean; id?: number } = { withIdentity: false }) {
      const item = await getDetails(params);
      this.setCurrent(item);
      return item;
    },
    setTokens(keycloak: any): void {
      this.token = keycloak.token;
      this.refreshToken = keycloak.refreshToken;
      this.idToken = keycloak.idToken;
      instance.defaults.headers.Authorization = `Bearer ${keycloak.token}`;
      LocalStorage.set(["auth", "token"], keycloak.token);
      LocalStorage.set(["auth", "refreshToken"], keycloak.refreshToken);
      LocalStorage.set(["auth", "idToken"], keycloak.idToken);
    },
    resetTokens() {
      this.token = null;
      this.refreshToken = null;
      delete instance.defaults.headers.Authorization;
    },
    setCurrentRole(role: string): void {
      this.currentRole = role;
    },
    composeAcceptedAgreements() {
      this.current.profileAgreement.forEach((i: profileAgreementType) => {
        if (i.agreement.id) {
          this.current.acceptedAgreements.push(i.agreement.id);
        }
      });
    },
    async update(): Promise<void> {
      console.log(this.current);

      const item = await updateUser(this.current);
      this.setCurrent(item);
      return item;
    },
    async logout(): Promise<void> {
      await logout();
      this.resetCurrent();
      this.resetTokens();
      clearToken();
      sessionStorage.clear();
      // await router.push({ name: "welcome" });
      // const page = { name: "welcome" };
      // await router.push(page);
      // this.resetCurrent();
      // await keycloak.logout();
      // this.resetTokens();
    },
    async getRoles(retries = 5, delay = 2000): Promise<void> {
      try {
      
        const {
          roles: rolesWithStatus,
          // network: network,
          missingAgreements: missingAgreements,
          // lastUsedRole: lastUsedRole,
          privileges: privileges,
          permissions: permissions
        } = await useSettings().init();

        const roles = await getRoles();
        
        roles.forEach((role: any) => {
          const current = rolesWithStatus.find((r) => r.id === role.roleId);
          if (current) {
            role.status = current.status;
            role.roleName = current.name;
            role.isSystemRole = current.isSystemRole;
            role.page = current.page;
          }
        });

        this.availableRoles = rolesWithStatus
          .filter((r) => !r.isSystemRole)
          .map((r) => {
            return {
              id: r.id,
              name: r.name,
            };
          });

        this.roles = roles;
        this.missingAgreements = missingAgreements;
        this.permissions = permissions;
        // this.network = network;
        // this.currentRole = lastUsedRole ?? this.getHigherRoleName;
        this.privileges = privileges;

        const settings = await getProfileSetting("lastUsedRole");

        if (settings.length > 0) {
          const roleFound = this.roles.find((r) => r.roleId == settings[0].value) || null;
          if (roleFound) {
            this.setSavedRole({ id: roleFound.roleId, name: roleFound.roleName });
          }
        } else {
          const activeRoles = this.roles.filter((r) => r.status === "Active" && !r.isSystemRole);
          if (activeRoles.length > 0) {
            const roleFound = this.roles.find((r) => r.roleName == activeRoles[0].roleName);
            if (roleFound) {
              this.setSavedRole({ id: roleFound.roleId, name: roleFound.roleName });
              await this.updateSavedRole(roleFound.roleId);
            }
          }
        }
      } catch (error) {
        console.error(error);
      }
    },
    setSavedRole(role: { id: number; name: string }) {
      this.savedRole = { ...role };
    },
    async deleteProfileSetting(settingName: string) {
      const settings = await getProfileSetting(settingName);
      if (settings.length > 0) {
        await deleteProfileSetting(settings[0].id);
        this.savedRole = null;
      }
    },
    async updateSavedRole(roleId: number) {
      await this.deleteProfileSetting("lastUsedRole");
      return addProfileSetting({
        section: "lastUsedRole",
        setting: "id",
        value: `${roleId}`,
      });
    },
    // function to check if a certain id is the one of the currently logged user
    isLoggedUser(id: string) {
      return this.current.id === id;
    },
  },
  getters: {
    isLoggedIn: (state): boolean => {
      return keycloak.token != null;
    },
    getSavedRoleName: (state) => {
      return state.savedRole?.name.toLowerCase() || "";
    },
    getPermissions(): Array<permissionType> {
      return this.permissions;
    },
    isFinalUser: (state) => state.roles.some(matchesProperty("roleName", "FinalUser")),
    nextIncompleteRole: (state) => state.roles.find(matchesProperty("status", "Pending")),
    isSuspended: (state): boolean =>
      state.roles.some(
        matchesProperty("status", "Suspended")
      ) /* if user has at least one role with Suspended */,
    isPending: (state): boolean => state.roles.some(matchesProperty("status", "Pending")),
    isWaiting: (state) =>
      state.roles.some(
        matchesProperty("status", "Waiting")
      ) /* if user has at least one role with Waiting */,
    isBackofficeUser: (state) => {
      let res = state.roles.filter(
        (r) => r.status == "Active" && ["BackofficeUser"].includes(r.roleName)
      );
      return !!res.length;
    },
    isAdministrator: (state) => {
      const res = state.roles.filter(
        (r) => r.status == "Active" && ["Administrator"].includes(r.roleName)
      );
      return !!res.length;
    },
    isProfessional: (state) => {
      const res = state.roles.filter(
        (r) => r.status == "Active" && ["Professional"].includes(r.roleName)
      );
      return !!res.length;
    },
    hasSelectedDesignerRole(): boolean {
      return this.getSavedRoleName.toLowerCase() == "designer";
    },
    hasSelectedProfessionalRole (): boolean {
      return this.getSavedRoleName.toLowerCase() == 'professional';
    },
    hasSelectedAdministratorRole (): boolean {
      return this.getSavedRoleName.toLowerCase() == 'administrator';
    },
    hasSelectedBackofficeUserRole (): boolean {
      return this.getSavedRoleName.toLowerCase() == 'backofficeuser';
    },
    hasSelectedOwnerRole (): boolean {
      return this.getSavedRoleName.toLowerCase() == 'mangoowner';
    },
    hasSelectedTenantRole (): boolean {
      return this.getSavedRoleName.toLowerCase() == 'mangotenant';
    },
    isUserWithRelevantActiveAccess(): boolean {
      if (this.isPending || this.isSuspended) return false;
      return this.isBackofficeUser;
    },
    getHigherRoleId: (state): number => {
      if (state.currentRole) {
        return state.roles.find((r) => r.roleName === state.currentRole)?.role_id || 1;
      } else {
        return state.roles.filter((r) => r.role_id != 1 && r.status === "Active").length > 0
          ? state.roles
              .filter((r) => r.role_id != 1)
              .reduce((max, r) => (r.role_id > max ? r.role_id : max), 1)
          : 1;
      }
    },
    getHigherRoleName: (state): string => {
      if (state.currentRole) {
        return state.currentRole;
      } else {
        const activeRoles = state.roles.filter((r) => r.role_id != 1 && r.status === "Active");
        if (activeRoles.length > 0) {
          const highestRole = activeRoles.reduce(
            (max, r) => (r.role_id > max.role_id ? r : max),
            activeRoles[0]
          );
          return highestRole.roleName;
        } else {
          return "-";
        }
      }
    },
    isHidden: (state) => {
      return (attribute: string, resource: string): boolean => {
        if (state.privileges.length == 0) return false;
        const res = state.privileges
          ?.find((p) => p.resource == resource)
          ?.attributes?.find((a) => a.name.toLowerCase() == attribute.toLowerCase());
        return res?.status == "Hidden";
      };
    },
    isDisabled: (state) => {
      return (attribute: string, resource: string): boolean => {
        if (state.privileges.length == 0) return false;
        const res = state.privileges
          ?.find((p) => p.resource == resource)
          ?.attributes?.find((a) => a.name.toLowerCase() == attribute.toLowerCase());
        return res?.status == "Disabled";
      };
    },
    isVisible: (state) => {
      return (attribute: string, resource: string): boolean => {
        if (state.privileges.length == 0) return false;
        const res = state.privileges
          ?.find((p) => p.resource == resource)
          ?.attributes?.find((a) => a.name.toLowerCase() == attribute.toLowerCase());
        return res?.status == "Enabled";
      };
    },
    hasWaitingRole: (state) => {
      return (roleName: string) =>
        state.roles.filter((r) => r.status == "Waiting" && [roleName].includes(r.roleName))
          ?.length > 0;
    },
    hasAccessToApplication: (state): boolean => {
      if (state.roles?.length > 0) {
        return !state.roles.every((r) => !!r.isSystemRole);
      } else {
        return false;
      }
    },
    professionalCategoryOptions: (state) =>
      professionalCategories
        .map((value) => {
          return {
            text: value.label,
            value: value.key,
          };
        })
        .sort((a, b) => (a.text > b.text ? 1 : -1)),
  },
});
